<template>
  <v-container class="my-3 px-7">
    <div v-if="loading">
      <v-skeleton-loader type="table-thead" />
      <v-skeleton-loader type="table-row-divider" />
      <v-skeleton-loader
        v-for="n in itemsPerPage"
        :key="n"
        type="table-row-divider"
      />
      <v-skeleton-loader type="table-tfoot" />
    </div>

    <v-data-table-server
      v-else
      :headers="headers"
      :items="data"
      :loading="loading"
      :sort-by="[{ key: sort, order: order }]"
      :hide-default-footer="true"
      :show-expand="hasExpand"
      item-value="id"
      @update:options="onUpdateOptions"
      hover
    >
      <!-- Acciones -->
      <template #item.actions="{ item }">
        <v-menu>
          <template #activator="{ props }">
            <v-btn icon v-bind="props" variant="text">
              <v-icon>mdi-dots-horizontal</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="editItem(item)">
              <v-list-item-title>Editar</v-list-item-title>
            </v-list-item>
            <v-list-item @click="deleteItem(item)">
              <v-list-item-title>Eliminar</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <!-- Slots especiales -->
      <template v-slot:[`item.user`]="{ item }">
        {{
          item?.attributes
            ? item?.attributes?.user?.name + (item?.attributes?.user?.last_name ? ' ' + item?.attributes?.user?.last_name : '')
            : item?.user?.name + (item?.user?.last_name ? ' ' + item?.user?.last_name : '')
        }}
      </template>
      <template v-slot:[`item.client`]="{ item }">
        {{item.client?.attributes?.commercial_name || item.client?.attributes?.legal_name}}
      </template>
      <template v-slot:[`item.contact`]="{ item }">
        {{item?.contact!=null?(item?.contact?.name + (item?.contact?.last!=null?(' ' + item?.contact?.last):'')):''}}
      </template>
      <template v-slot:[`item.subject`]="{ item }">
        {{ item?.subject?.attributes?.commercial_name || item?.subject?.attributes?.legal_name || item?.subject?.attributes?.name || '' }}
      </template>
      <template v-slot:[`item.product`]="{ item }">
        {{item?.description}} 
        <v-chip v-if="getOrganizationName(item)" label size="x-small" class="ml-2">
          {{ getOrganizationName(item) }}
        </v-chip>
      </template>
      <template v-slot:[`item.attributes.client_type`]="{ item }">
        {{item?.attributes?.client_type?.name}}
      </template>
      <template v-slot:[`item.attributes.agencies`]="{ item }">
        <v-chip-group>
          <v-chip size="small" label v-for="(agency, index) in item?.attributes?.agencies" :key="index">{{agency?.commercial_name || agency?.legal_name}}</v-chip>
        </v-chip-group>
      </template>
      <template v-slot:[`item.needs_authorization`]="{ item }">
          <v-chip size="x-small" color="orange" label v-if="item?.needs_authorization">Requiere Autorización</v-chip>
      </template>
      <template v-slot:[`item.attributes.accounts`]="{ item }">
        <v-chip-group>
          <v-chip size="small" label v-for="(account, index) in item?.attributes?.accounts" :key="index">{{account?.commercial_name || account?.legal_name}}</v-chip>
        </v-chip-group>
      </template>

      <!-- Formatos especiales -->
      <template
        v-for="header in headers.filter(h => h.format === 'currency')"
        :key="header.value"
        v-slot:[`item.${header.value}`]="{ item }"
      >
        {{ $toCurrency(item[header.value]) }}
      </template>
      <template
        v-for="header in headers.filter(h => h.format === 'percentage')"
        :key="header.value"
        v-slot:[`item.${header.value}`]="{ item }"
      >
        {{ Number(item[header.value]).toLocaleString('es-MX') + '%' }}
      </template>
      <template
        v-for="header in headers.filter(h => h.format === 'decimal')"
        :key="header.value"
        v-slot:[`item.${header.value}`]="{ item }"
      >
        {{ Number(item[header.value]).toLocaleString('es-MX') }}
      </template>

      <!-- Expandible -->
      <template v-slot:expanded-row="{ columns, item }" v-if="hasExpand">
        <tr>
          <td :colspan="columns.length" class="py-2">
            <TableView
              :headers="expandHeaders"
              :data="item[expandKey]"
            />
          </td>
        </tr>
      </template>

      <!-- Detalle -->
      <template v-if="hasDetail" v-slot:[`item.eye_detail`]="{ item }">
        <v-btn icon class="elevation-0" size="small" @click="$emit('viewItem', item.id)">
          <v-icon>
            mdi-eye
          </v-icon>
        </v-btn>
      </template>
    </v-data-table-server>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, PropType } from 'vue';
import { useLayoutField } from '@/composables/useLayoutField';
import { useFloatFormStore } from '@/store/floatForm';
import { useBottomSheetStore } from '@/store/bottomSheet';
import { configRegistry } from '@/lib/configRegistry';

export default defineComponent({
  name: 'DataTableModule',
  props: {
    moduleName: {
      type: String,
      required: true
    },
    headers: {
      type: Array as PropType<any[]>,
      required: true
    },
    data: {
      type: Array as PropType<any[]>,
      required: true
    },
    onUpdate: Function as PropType<(data: Record<string, any>) => Promise<any>>,
    onDelete: Function as PropType<(data: Record<string, any>) => Promise<any>>,
    title: Object,
  },
  setup(props) {
    const floatFormStore = useFloatFormStore();
    const bottomSheetStore = useBottomSheetStore();
    const loading = useLayoutField(props.moduleName, 'dataLoading');
    const itemsPerPage = useLayoutField(props.moduleName, 'itemsPerPage');
    const sort = useLayoutField(props.moduleName, 'sortBy');
    const order = useLayoutField(props.moduleName, 'direction');

    const hasExpand = ref(false);
    const expandHeaders = ref<any[]>([]);
    const expandKey = ref<string>('');
    const hasDetail = ref(false);

    onMounted(async () => {
      const loader = configRegistry[props.moduleName];
      if (!loader) return;

      const mod = await loader();
      //if (mod?.DETAIL?.expand_table) {
        hasExpand.value = mod.DETAIL.expand_table;
        expandHeaders.value = mod.EXPAND_HEADERS || [];
        expandKey.value = mod.DETAIL.key || '';
        hasDetail.value = mod.DETAIL.eye_detail;
      //}
    });

    const onUpdateOptions = (options: any) => {
      const sortObj = options?.sortBy?.[0];
      if (sortObj?.key && sortObj?.order) {
        sort.value = sortObj.key;
        order.value = sortObj.order;
      }
    };

    const editItem = (item: any) => {
      floatFormStore.openForm({
        component: 'EditForm',
        formTitle: 'Editar',
        formWidth: '1080',
        module: props.moduleName,
        props: {
          item,
          onUpdate: props.onUpdate
        }
      });
    };

    const sheet = ref(false);
    const itemToDelete = ref<any>(null);

    const deleteItem = (item: any) => {
      bottomSheetStore.openSheet({
        module: props.moduleName,
        formTitle: `¿Seguro(a) que deseas eliminar ${props.title?.singular + ' ' + item.id}?`,
        props: {
          item,
          message: 'Esta acción no se puede deshacer.',
          action: (item) => {
            if (!props.onDelete) {
              console.warn('No se proporcionó onDelete');
              return Promise.reject(new Error('No se proporcionó onDelete'));
            }
            return props.onDelete(item.id);
          }
        }
      });
    };

    const getOrganizationName = (item:any) => {
      return (
        item?.quoted_item?.attributes?.organization?.name ||
        item?.sold_item?.attributes?.organization?.name ||
        item?.quoted_item?.attributes?.product?.attributes?.organization?.name ||
        item?.sold_item?.attributes?.product?.attributes?.organization?.name ||
        null
      );
    };

    return {
      sort,
      order,
      loading,
      itemsPerPage,
      onUpdateOptions,
      editItem,
      deleteItem,
      hasExpand,
      expandHeaders,
      expandKey,
      hasDetail,
      getOrganizationName
    };
  }
});
</script>
